import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  Download, 
  Filter, 
  Plus, 
  Eye, 
  Calendar,
  Clock,
  ChevronDown,
  Crown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useDecisions } from "@/hooks/useDecisions";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";

interface LogProps {
  onNewDecision?: () => void;
  onUpgrade?: () => void;
}

interface ActivityItem {
  id: string;
  type: 'sign_in' | 'decision_created' | 'decision_viewed' | 'decision_updated' | 'review_completed';
  title: string;
  description: string;
  timestamp: string;
  icon: LucideIcon;
  metadata?: {
    decisionId?: string;
    decisionTitle?: string;
    rating?: number;
  };
}

export function Log({ onNewDecision, onUpgrade }: LogProps) {
  const { decisions } = useDecisions();
  const { user } = useAuth();
  const { canUseActivityLog, loading: subscriptionLoading } = useSubscription();
  const [selectedActivityType, setSelectedActivityType] = useState<string>("all");
  const [activities, setActivities] = useState<ActivityItem[]>([]);

  // Generate activity log from decisions and user actions
  useEffect(() => {
    const activityItems: ActivityItem[] = [];

    // Add decision-related activities
    decisions.forEach((decision) => {
      // Decision creation
      activityItems.push({
        id: `created-${decision.id}`,
        type: 'decision_created',
        title: `Created decision: ${decision.title}`,
        description: 'Added new decision about ' + (decision.description || decision.title),
        timestamp: decision.created_at,
        icon: Plus,
        metadata: {
          decisionId: decision.id,
          decisionTitle: decision.title,
          rating: decision.day0_rating,
        },
      });

      // Review activities
      decision.rating_history.forEach((rating) => {
        if (rating.day > 0) {
          activityItems.push({
            id: `review-${decision.id}-${rating.day}`,
            type: 'review_completed',
            title: `Completed ${rating.day}-day review`,
            description: `Reviewed "${decision.title}" and rated it ${rating.rating}/10`,
            timestamp: rating.date,
            icon: Eye,
            metadata: {
              decisionId: decision.id,
              decisionTitle: decision.title,
              rating: rating.rating,
            },
          });
        }
      });
    });

    // Sort by timestamp (most recent first)
    activityItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    setActivities(activityItems);
  }, [decisions, user]);

  // Filter activities based on selected type
  const filteredActivities = selectedActivityType === "all" 
    ? activities 
    : activities.filter(activity => activity.type === selectedActivityType);

  // Group activities by date
  const groupedActivities = filteredActivities.reduce((groups: { [key: string]: ActivityItem[] }, activity) => {
    const date = new Date(activity.timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let dateKey: string;
    if (date.toDateString() === today.toDateString()) {
      dateKey = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      dateKey = 'Yesterday';
    } else {
      dateKey = date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }

    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(activity);
    return groups;
  }, {});

  const getActivityTypeLabel = (type: string) => {
    switch (type) {
      case 'sign_in': return 'Sign In';
      case 'decision_created': return 'Decision Created';
      case 'decision_viewed': return 'Decision Viewed';
      case 'decision_updated': return 'Decision Updated';
      case 'review_completed': return 'Review Completed';
      default: return type;
    }
  };

  const getActivityTypeColor = (type: string) => {
    switch (type) {
      case 'sign_in': return 'bg-blue-100 text-blue-800';
      case 'decision_created': return 'bg-green-100 text-green-800';
      case 'decision_viewed': return 'bg-gray-100 text-gray-800';
      case 'decision_updated': return 'bg-yellow-100 text-yellow-800';
      case 'review_completed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const exportLog = (format: 'json' | 'csv') => {
    const logData = filteredActivities.map(activity => ({
      timestamp: activity.timestamp,
      type: activity.type,
      title: activity.title,
      description: activity.description,
      metadata: activity.metadata,
    }));

    if (format === 'json') {
      const dataStr = JSON.stringify(logData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-log-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } else if (format === 'csv') {
      // Convert to CSV format
      const headers = ['Timestamp', 'Type', 'Title', 'Description', 'Metadata'];
      const csvRows = [
        headers.join(','),
        ...logData.map(row => [
          `"${row.timestamp}"`,
          `"${row.type}"`,
          `"${row.title}"`,
          `"${row.description || ''}"`,
          `"${row.metadata ? JSON.stringify(row.metadata).replace(/"/g, '""') : ''}"`
        ].join(','))
      ];
      
      const csvStr = csvRows.join('\n');
      const dataBlob = new Blob([csvStr], { type: 'text/csv' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-log-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  // The activity log is premium-only. Rendered after every hook above so the
  // hook order stays stable between the gated and ungated views.
  if (!subscriptionLoading && !canUseActivityLog()) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1>Activity Log</h1>
          <p className="text-muted-foreground">
            Track your journey and see everything you've done in DecisionJournal
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5" />
              A premium feature
            </CardTitle>
            <CardDescription>
              The activity log records every decision you create, review and revisit,
              so you can look back at how your thinking changed over time.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Creating and reviewing decisions stays unlimited on the free plan.
              Upgrading adds the activity log and removes the cap on review dates
              per decision.
            </p>
            {onUpgrade && (
              <Button onClick={onUpgrade}>
                <Crown className="h-4 w-4 mr-2" />
                Upgrade
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1>Activity Log</h1>
          <p className="text-muted-foreground">
            Track your journey and see everything you've done in DecisionJournal
          </p>
        </div>
        <div className="flex items-center gap-3">
          {onNewDecision && (
            <Button onClick={onNewDecision}>
              <Plus className="h-4 w-4 mr-2" />
              New Decision
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Export Log
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportLog('json')}>
                <Download className="h-4 w-4 mr-2" />
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportLog('csv')}>
                <Download className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filter Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filter Activities
          </CardTitle>
          <CardDescription>Choose which types of activities to display in your log</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={selectedActivityType} onValueChange={setSelectedActivityType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Activities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="decision_created">Decision Created</SelectItem>
                <SelectItem value="review_completed">Review Completed</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {filteredActivities.length} activities
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <div className="space-y-6">
        {Object.entries(groupedActivities).map(([dateKey, dayActivities]) => (
          <div key={dateKey}>
            {/* Date Header */}
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium text-muted-foreground">{dateKey}</h3>
            </div>

            {/* Activities for this date */}
            <div className="space-y-3 ml-6 border-l border-border pl-6">
              {dayActivities.map((activity) => {
                const IconComponent = activity.icon;
                return (
                  <div key={activity.id} className="relative">
                    {/* Timeline dot */}
                    <div className="absolute -left-[33px] top-3 w-3 h-3 bg-primary rounded-full border-2 border-background"></div>
                    
                    {/* Activity content */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{activity.title}</span>
                            <Badge variant="outline" className={`text-xs ${getActivityTypeColor(activity.type)}`}>
                              {getActivityTypeLabel(activity.type)}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{activity.description}</p>
                          {activity.metadata?.rating && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                              <Eye className="h-3 w-3" />
                              <span>Rating: {activity.metadata.rating}/10</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground whitespace-nowrap ml-4">
                        <div>{formatTime(activity.timestamp)}</div>
                        <div>{formatDate(activity.timestamp)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {filteredActivities.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Clock className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No activities found</p>
              <p className="text-sm text-muted-foreground">Start using the app to see your activity log</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}